import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, MapPin, Search, X, Check, PlusCircle, Loader2 } from "lucide-react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * V31 — Home Tuition Location Picker
 *
 *  - Searchable single-select City dropdown (from /api/home-tuition/cities)
 *  - City-scoped PIN code multi-select with locality search
 *      (from /api/home-tuition/cities/{slug}/pin-codes?q=)
 *  - Manual PIN entry for codes not in the curated list
 *
 * Props:
 *  - city: current city slug (e.g. "jabalpur")
 *  - onCityChange(newSlug)
 *  - pins: array of selected PIN strings
 *  - onPinsChange(newPins)
 *  - error: optional inline error message
 *  - testid: testid prefix
 */
export default function HomeTuitionLocationPicker({
    city, onCityChange,
    pins, onPinsChange,
    cityError, pinError,
    testid = "ht-loc",
}) {
    const [cities, setCities] = useState([]);
    const [cityOpen, setCityOpen] = useState(false);
    const [citySearch, setCitySearch] = useState("");
    const [pinSearch, setPinSearch] = useState("");
    const [pinOptions, setPinOptions] = useState([]);
    const [pinLoading, setPinLoading] = useState(false);
    const cityBoxRef = useRef(null);

    // Load cities once
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API_BASE}/home-tuition/cities`)
            .then((r) => { if (!cancelled) setCities(r.data?.cities || []); })
            .catch(() => { if (!cancelled) setCities([]); });
        return () => { cancelled = true; };
    }, []);

    // Close city popup on outside click
    useEffect(() => {
        const onDocClick = (e) => {
            if (cityBoxRef.current && !cityBoxRef.current.contains(e.target)) setCityOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    // Load PIN options whenever city changes or user types in search
    const reloadPins = useCallback(async (slug, q) => {
        if (!slug) { setPinOptions([]); return; }
        setPinLoading(true);
        try {
            const url = `${API_BASE}/home-tuition/cities/${slug}/pin-codes`
                + (q ? `?q=${encodeURIComponent(q)}` : "");
            const r = await axios.get(url);
            setPinOptions(r.data?.items || []);
        } catch {
            setPinOptions([]);
        } finally { setPinLoading(false); }
    }, []);

    useEffect(() => { reloadPins(city, ""); }, [city, reloadPins]);

    // Debounced search
    useEffect(() => {
        if (!city) return undefined;
        const t = setTimeout(() => reloadPins(city, pinSearch.trim()), 200);
        return () => clearTimeout(t);
    }, [pinSearch, city, reloadPins]);

    const cityObj = cities.find((c) => c.slug === city);
    const filteredCities = useMemo(() => {
        const q = citySearch.trim().toLowerCase();
        if (!q) return cities;
        return cities.filter((c) => c.name.toLowerCase().includes(q));
    }, [cities, citySearch]);

    const togglePin = (pin) => {
        const next = pins.includes(pin) ? pins.filter((p) => p !== pin) : [...pins, pin];
        onPinsChange(next);
    };

    const removePin = (pin) => onPinsChange(pins.filter((p) => p !== pin));

    // Manual entry — show prompt when search is a valid 6-digit pin not already in options
    const isManualEligible = useMemo(() => {
        const q = pinSearch.replace(/\D/g, "");
        return q.length === 6 && !pinOptions.some((o) => o.pin_code === q) && !pins.includes(q);
    }, [pinSearch, pinOptions, pins]);

    const addManualPin = () => {
        const q = pinSearch.replace(/\D/g, "");
        if (q.length !== 6) return;
        if (!pins.includes(q)) onPinsChange([...pins, q]);
        setPinSearch("");
    };

    return (
        <div className="space-y-3" data-testid={`${testid}-root`}>
            {/* ---- City selector ---- */}
            <div ref={cityBoxRef}>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1 block">
                    Home Tuition City <span className="text-red-500">*</span>
                </label>
                <button
                    type="button"
                    onClick={() => setCityOpen((o) => !o)}
                    className={`w-full inline-flex items-center justify-between px-3 py-2 rounded-lg border-2 bg-white text-left text-sm transition ${
                        cityError ? "border-red-400" : "border-slate-200 focus:border-[#1E3A8A]"
                    }`}
                    data-testid={`${testid}-city-button`}
                >
                    <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-[#1E3A8A]" />
                        {cityObj ? cityObj.name : <span className="text-slate-400">Select your service city…</span>}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition ${cityOpen ? "rotate-180" : ""}`} />
                </button>
                {cityError && <div className="text-[11px] text-red-600 mt-1">{cityError}</div>}
                {cityOpen && (
                    <div className="relative">
                        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg" data-testid={`${testid}-city-list`}>
                            <div className="p-2 border-b border-slate-100">
                                <div className="relative">
                                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-slate-400" />
                                    <input
                                        type="text"
                                        value={citySearch}
                                        onChange={(e) => setCitySearch(e.target.value)}
                                        placeholder="Search city…"
                                        className="w-full pl-8 pr-2 py-1.5 text-sm rounded border border-slate-200 focus:outline-none focus:border-[#1E3A8A]"
                                        autoFocus
                                        data-testid={`${testid}-city-search`}
                                    />
                                </div>
                            </div>
                            <div className="max-h-56 overflow-y-auto">
                                {filteredCities.length === 0 ? (
                                    <div className="px-3 py-2 text-xs text-slate-400 italic">No matching city.</div>
                                ) : filteredCities.map((c) => (
                                    <button
                                        key={c.slug}
                                        type="button"
                                        onClick={() => {
                                            onCityChange(c.slug);
                                            setCityOpen(false);
                                            setCitySearch("");
                                            // Clear PINs whenever the user picks a different city
                                            if (c.slug !== city) onPinsChange([]);
                                        }}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 inline-flex items-center justify-between ${
                                            c.slug === city ? "bg-blue-50/60" : ""
                                        }`}
                                        data-testid={`${testid}-city-opt-${c.slug}`}
                                    >
                                        <span>{c.name}</span>
                                        {c.slug === city && <Check className="w-3.5 h-3.5 text-[#1E3A8A]" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ---- PIN code multi-select ---- */}
            <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1 block">
                    Teaching Area PIN Code(s) <span className="text-red-500">*</span>
                </label>

                {/* Selected pills */}
                {pins.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testid}-selected`}>
                        {pins.map((p) => (
                            <span key={p}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono bg-emerald-100 border border-emerald-400 text-emerald-900"
                                  data-testid={`${testid}-pill-${p}`}>
                                {p}
                                <button type="button" onClick={() => removePin(p)} className="hover:text-red-700" data-testid={`${testid}-remove-${p}`}>
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {city ? (
                    <>
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                            <input
                                type="text"
                                value={pinSearch}
                                onChange={(e) => setPinSearch(e.target.value)}
                                placeholder="Search PIN code or locality (e.g. 482001 or Civil Lines)"
                                className={`w-full pl-8 pr-3 py-2 text-sm rounded-lg border-2 bg-white focus:outline-none transition ${
                                    pinError ? "border-red-400" : "border-slate-200 focus:border-[#1E3A8A]"
                                }`}
                                data-testid={`${testid}-pin-search`}
                            />
                        </div>
                        {pinError && <div className="text-[11px] text-red-600 mt-1">{pinError}</div>}

                        <div className="mt-2 max-h-44 overflow-y-auto border border-slate-100 rounded-lg" data-testid={`${testid}-pin-options`}>
                            {pinLoading ? (
                                <div className="px-3 py-3 text-xs text-slate-400 inline-flex items-center gap-2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
                                </div>
                            ) : pinOptions.length === 0 ? (
                                <div className="px-3 py-3 text-xs text-slate-400 italic">No PIN codes match your search.</div>
                            ) : pinOptions.map((opt) => {
                                const selected = pins.includes(opt.pin_code);
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => togglePin(opt.pin_code)}
                                        className={`w-full text-left px-3 py-1.5 text-xs inline-flex items-center gap-2 hover:bg-slate-50 ${
                                            selected ? "bg-emerald-50/70" : ""
                                        }`}
                                        data-testid={`${testid}-pin-opt-${opt.pin_code}`}
                                    >
                                        <span className={`inline-flex items-center justify-center w-4 h-4 rounded border ${
                                            selected ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300"
                                        }`}>
                                            {selected && <Check className="w-2.5 h-2.5" />}
                                        </span>
                                        <span className="font-mono font-semibold">{opt.pin_code}</span>
                                        <span className="text-slate-500">{opt.locality}</span>
                                        {opt.source === "tutor_added" && (
                                            <span className="ml-auto text-[9px] uppercase font-bold tracking-wider px-1.5 rounded-full bg-amber-100 text-amber-800">tutor-added</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Manual entry hint */}
                        {isManualEligible && (
                            <button
                                type="button"
                                onClick={addManualPin}
                                className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#1E3A8A] hover:text-[#1E40AF] underline underline-offset-2"
                                data-testid={`${testid}-add-manual`}
                            >
                                <PlusCircle className="w-3 h-3" /> PIN code not found? You can manually add your teaching PIN code <span className="font-mono">{pinSearch.replace(/\D/g, "")}</span>.
                            </button>
                        )}
                    </>
                ) : (
                    <div className="text-xs text-slate-400 italic px-2 py-3 border border-dashed border-slate-200 rounded-lg">
                        Select a city above to see its PIN codes.
                    </div>
                )}
            </div>
        </div>
    );
}
